import type {
  ButtonProps,
  CSSVariablesResolver,
  MantineTheme,
} from '@mantine/core';
import {
  createTheme,
  mergeThemeOverrides,
  rgba,
  v8CssVariablesResolver,
} from '@mantine/core';
import { palette } from './palette';
import { typography } from './typography';
import switchClasses from './Switch.module.css';
import actionIconClasses from './ActionIcon.module.css';
import buttonClasses from './Button.module.css';
import notificationClasses from './Notification.module.css';

const colors = createTheme({ colors: palette });

const components = {
  components: {
    SegmentedControl: {
      defaultProps: {
        styles: {
          root: {
            lineHeight: '24px',
          },
        },
      },
    },
    ActionIcon: {
      defaultProps: {
        variant: 'subtle',
        radius: '50%',
      },
      classNames: {
        root: actionIconClasses.root,
        icon: actionIconClasses.icon,
      },
    },
    Notification: {
      classNames: {
        description: notificationClasses.description,
      },
    },
    Button: {
      classNames: {
        root: buttonClasses.root,
        inner: buttonClasses.inner,
        label: buttonClasses.label,
      },
      styles: {
        root: {
          fontWeight: 500,
        },
      },
      vars: (_theme: MantineTheme, props: ButtonProps) => {
        if (props.size === 'md') {
          return {
            root: {
              '--button-height': '40px',
            },
          };
        }

        if (props.size === 'sm') {
          return {
            root: {
              '--button-height': '32px',
            },
          };
        }

        return { root: {} };
      },
    },
    Popover: {
      defaultProps: {
        hideDetached: false,
      },
    },
    Tabs: {
      styles: {
        tabLabel: {
          textAlign: 'start',
        },
      },
    },
    Switch: {
      defaultProps: {
        withThumbIndicator: false,
      },
      classNames: {
        body: switchClasses.body,
        label: switchClasses.label,
        track: switchClasses.track,
        thumb: switchClasses.thumb,
      },
    },
  },
};

/**
 * Alias of {@link useTheme} — the `use` prefix trips `react-hooks/rules-of-hooks`
 * even though this is a plain function. Use `getMantineTheme` at module scope or
 * anywhere outside a component/custom hook.
 */
export const getMantineTheme = (): MantineTheme => buildTheme();

export const useTheme = (): MantineTheme => buildTheme();

const buildTheme = (colorMode: 'light' | 'dark' = 'light'): MantineTheme => {
  const extendedColors = palette;
  const isLight = colorMode === 'light';
  const white = '#FFFFFF';
  const theme = createTheme({
    ...components,
    headings: {
      fontFamily: '"Gilroy-SemiBold"',
    },
    fontFamily: '"Inter"',
    fontWeights: {
      regular: '400',
      medium: '500',
      bold: '700',
    },
    fontSizes: {
      xxs: '0.625rem',
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      xxl: '1.5rem',
    },
    lineHeights: {
      xs: '16px',
      sm: '24px',
      md: '24px',
      lg: '32px',
      xl: '48px',
    },
    defaultRadius: '0.25rem',
    radius: {
      xs: '0.125rem',
      sm: '0.25rem',
      md: '0.5rem',
      lg: '1rem',
      xl: '1.5rem',
    },
    spacing: {
      xxs: '0.25rem',
      xs: '0.5rem',
      sm: '0.75rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      xxl: '3rem',
    },
    primaryColor: 'tealBrand',
    primaryShade: {
      light: 7,
      dark: 9,
    },
    cursorType: 'pointer',
    other: {
      minWidth: '352px',
      mainColor: {
        foreground: {
          primary: isLight ? extendedColors.onSurface[0] : white,
          secondary: isLight
            ? extendedColors.onSurface[1]
            : extendedColors.grayBrand[3],
          tertiary: isLight
            ? extendedColors.grayBrand[5]
            : extendedColors.grayBrand[4],
          onAccent: isLight ? white : extendedColors.grayBrand[8],
          disabled: extendedColors.grayBrand[5],
        },
        background: {
          primary: isLight ? white : extendedColors.grayBrand[8],
          primaryHover: extendedColors.tealBrand[0],
          secondary: isLight
            ? extendedColors.grayBrand[0]
            : extendedColors.grayBrand[7],
          tertiary: isLight
            ? extendedColors.grayBrand[2]
            : extendedColors.grayBrand[6],
          disabled: extendedColors.grayBrand[2],
        },
        accent: {
          normal: isLight
            ? extendedColors.tealBrand[7]
            : extendedColors.tealBrand[3],
          hover: isLight
            ? extendedColors.tealBrand[6]
            : extendedColors.tealBrand[3],
          pressed: isLight
            ? extendedColors.tealBrand[8]
            : extendedColors.tealBrand[0],
          secondaryHover: isLight
            ? rgba(extendedColors.tealBrand[6], 0.1)
            : rgba(extendedColors.tealBrand[2], 0.1),
          secondaryPressed: isLight
            ? rgba(extendedColors.tealBrand[6], 0.2)
            : rgba(extendedColors.tealBrand[2], 0.2),
          tertiaryHover: isLight
            ? rgba(extendedColors.tealBrand[6], 0.05)
            : rgba(extendedColors.tealBrand[2], 0.05),
        },
        outline: {
          primary: extendedColors.outlineColor[0],
          secondary: extendedColors.outlineColor[1],
        },
      },
      statusColor: {
        error: isLight
          ? extendedColors.redBrand[6]
          : extendedColors.redBrand[1],
      },
      riskColor: {
        high: isLight ? extendedColors.redBrand[5] : extendedColors.redBrand[1],
        medium: extendedColors.orangeBrand[5],
        low: extendedColors.yellowBrand[5],
        minor: extendedColors.grayBrand[6],
        none: extendedColors.blueBrand[3],
        unknown: extendedColors.grayBrand[3],
      },
      riskColorText: {
        high: extendedColors.redBrand[8],
        medium: extendedColors.orangeBrand[9],
        low: extendedColors.yellowBrand[9],
        minor: extendedColors.grayBrand[7],
        none: extendedColors.blueBrand[8],
        unknown: extendedColors.grayBrand[7],
      },
      overlay: {
        background: rgba(extendedColors.grayBrand[8], 0.8),
        foreground: extendedColors.surface[0],
        accent: extendedColors.tealBrand[2],
      },
      surfaceColor: {
        surface: extendedColors.surface[2],
        surfaceDim: extendedColors.surface[7],
        surfaceBright: extendedColors.surface[1],
        surfaceContainerLowest: extendedColors.surface[0],
        surfaceContainerLow: extendedColors.surface[3],
        surfaceContainer: extendedColors.surface[4],
        surfaceContainerHigh: extendedColors.surface[5],
        surfaceContainerHighest: extendedColors.surface[6],
      },
      helpColor: {
        tooltip: extendedColors.custom[1],
      },
      workActivityColor: {
        goodBackground: extendedColors.tealBrand[0],
        goodForeground: extendedColors.tealBrand[7],
        badBackground: '#FFF2F7',
        badForeground: '#FF4C97',
      },
      workTypeColor: {
        herbicide: '#CE51D9',
        mow: '#FAE953',
        cut: '#81DB6F',
        sideWall: '#5BD3E4',
        noWorkType: '#F4EAEB',
      },
      banner: {
        title: extendedColors.yellowBrand[9],
        background: extendedColors.yellowBrand[0],
        border: extendedColors.yellowBrand[3],
      },
      shadow: {
        color: isLight ? 'rgba(106, 116, 142, 0.20)' : 'rgba(18, 19, 20, 0.20)',
        mainColorBackgroundPrimary: isLight
          ? '0px 4px 16px 0px rgba(106, 116, 142, 0.20)'
          : '0px 4px 16px 0px rgba(18, 19, 20, 0.20)',
        mainColorBackgroundSecondary: isLight
          ? '0px 4px 16px 0px rgba(106, 116, 142, 0.20)'
          : '0px 4px 16px 0px rgba(18, 19, 20, 0.20)',
        mainColorBackgroundTertiary: isLight
          ? '0px 4px 16px 0px rgba(106, 116, 142, 0.20)'
          : '0px 4px 16px 0px rgba(18, 19, 20, 0.20)',
        mainColorAccentNormal: isLight
          ? '0px 4px 16px 0px rgba(106, 116, 142, 0.20)'
          : '0px 4px 16px 0px rgba(49, 226, 188, 0.30)',
        mainColorAccentHover: isLight
          ? '0px 4px 24px 4px rgba(17, 124, 101, 0.25)'
          : '0px 4px 24px 4px rgba(49, 226, 188, 0.25)',
      },
      // TODO: Deprecate these in favor of the proper Mantine theme variables
      spacing: {
        xxs: '4px',
        xs: '8px',
        sm: '12px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        xxl: '48px',
      },
      opacity: {
        hover: 0.08,
        pressed: 0.12,
      },
      iconSize: {
        xs: 12,
        sm: 16,
        md: 20,
        lg: 24,
      },
      ...typography,
    },
  });

  return mergeThemeOverrides(theme, colors) as MantineTheme;
};

// Swiped from Corgi:
// https://github.com/20treeAI/corgi/blob/1b1412beac42899b065d829c4812ba44dc74a5b5/client/src/assets/styles/themeMantine.ts#L260
export const flattenObject = <T extends object>(
  obj: T,
  prefix: string,
  parentKey = ''
): Record<string, string> => {
  const flattened: Record<string, string> = {};
  const entries: [string, unknown][] = Object.entries(obj);
  for (const [key, value] of entries) {
    const fullPath = parentKey ? `${parentKey}-${key}` : `${prefix}${key}`;
    if (typeof value === 'object' && value !== null) {
      Object.assign(flattened, flattenObject(value, prefix, fullPath));
    } else {
      flattened[fullPath] = String(value);
    }
  }
  return flattened;
};

/**
 *
 * This will automatically translate all leaf nodes in the 'other'
 * object within the theme, as defined above, into css variables.
 * e.g.
 * --os-borderRadius-xs,
 * --os-typography-variants-bodySemiBold-letterSpacing,
 * --os-typography-variants-bodyXl-fontFamily
 *
 * These variables are then used in css modules to provide
 * custom styling.
 */
export const resolver: CSSVariablesResolver = (theme) => {
  const v8 = v8CssVariablesResolver(theme);
  return {
    variables: { ...v8.variables, ...flattenObject(theme.other, '--os-') },
    light: v8.light,
    dark: v8.dark,
  };
};
